// src/pages/NotaireDossier.jsx
// Drill-down d'un dossier notaire : œuvre par œuvre, avec les pièces présentes /
// manquantes. Données via notaire_dossier_oeuvres (RPC gatée par permission ;
// aucune valeur financière exposée). Route /notaire/dossier/:clientId.
import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import {
  generateNotaireDossierPDF,
  generateNotaireDossierXLSX,
} from "../lib/NotaireDossierExport";
import NotaireTransmissionCartes from "../components/NotaireTransmissionCartes";

function Chip({ ok, label }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${
        ok ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
           : "bg-amber-50 text-amber-700 border border-amber-200"
      }`}
    >
      {ok ? "✓" : "✗"} {label}
    </span>
  );
}

export default function NotaireDossier() {
  const { clientId } = useParams();
  const location = useLocation();
  const nom = location.state?.nom || "Dossier";

  const [oeuvres, setOeuvres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);

  const exportDossier = async (kind) => {
    setExporting(true);
    try {
      const payload = { nom, oeuvres };
      if (kind === "pdf") generateNotaireDossierPDF(payload);
      else await generateNotaireDossierXLSX(payload);
    } catch (e) {
      console.error("[notaire] export dossier:", e);
      alert("L'export n'a pas pu être généré. Réessayez dans quelques instants.");
    } finally {
      setExporting(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase.rpc("notaire_dossier_oeuvres", { p_client_id: clientId });
      if (err) throw err;
      setOeuvres(data || []);
    } catch (e) {
      console.error("[notaire] dossier détail:", e);
      setError("Impossible de charger ce dossier.");
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const total = oeuvres.length;
  const completes = oeuvres.filter((o) => o.manques === 0).length;
  const aReunir = oeuvres.filter((o) => o.manques > 0).length;

  return (
    <div className="min-h-screen bg-[#f6f6f4] text-gray-900" style={{ fontFamily: "Georgia, serif" }}>
      <header className="sticky top-0 z-20 border-b border-gray-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4">
          <div className="min-w-0">
            <Link to="/notaire" className="text-sm text-gray-500 hover:text-gray-900">← Tous les dossiers</Link>
            <h1 className="text-2xl font-bold text-gray-950 truncate">{nom}</h1>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => exportDossier("pdf")} disabled={exporting || loading || oeuvres.length === 0}
              className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60">
              {exporting ? "Génération…" : "Export PDF"}
            </button>
            <button onClick={() => exportDossier("xlsx")} disabled={exporting || loading || oeuvres.length === 0}
              className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60">
              {exporting ? "Génération…" : "Export Excel"}
            </button>
            <button onClick={load} disabled={loading}
              className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60">
              {loading ? "Actualisation…" : "Actualiser"}
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6 py-8">
        <section className="grid grid-cols-3 gap-4 mb-8">
          {[
            ["Œuvres", total],
            ["Dossier complet", completes],
            ["À compléter", aReunir],
          ].map(([label, value]) => (
            <div key={label} className="rounded-2xl border border-gray-200 bg-white p-5">
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</div>
              <div className="mt-2 text-3xl font-bold text-gray-950">{loading ? "…" : value}</div>
            </div>
          ))}
        </section>

        {error && (
          <div className="mb-6 rounded-xl border border-rose-200 bg-rose-50 px-5 py-4 text-sm text-rose-700">{error}</div>
        )}

        {!loading && !error && total === 0 && (
          <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-10 text-center">
            <h2 className="text-lg font-bold text-gray-900 mb-2">Aucune œuvre à afficher</h2>
            <p className="text-sm text-gray-500 max-w-md mx-auto">
              Ce dossier ne contient pas encore d'œuvre, ou l'accès ne vous a pas été accordé.
            </p>
          </div>
        )}

        {total > 0 && (
          <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">Œuvre</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">Pièces au dossier</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-600">Manques</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {oeuvres.map((o) => (
                    <tr key={o.id} className={o.manques > 0 ? "" : "bg-emerald-50/30"}>
                      <td className="px-6 py-4">
                        <div className="font-semibold text-gray-900">{o.titre || "Sans titre"}</div>
                        {o.artiste && <div className="text-sm text-gray-500">{o.artiste}</div>}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1.5">
                          <Chip ok={o.has_photo} label="Photo" />
                          <Chip ok={o.has_facture} label="Facture" />
                          <Chip ok={o.has_certificat} label="Certificat" />
                          <Chip ok={o.has_provenance} label="Provenance" />
                          {o.a_verifier && (
                            <span
                              className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium bg-orange-50 text-orange-700 border border-orange-200"
                              title={o.verification_note || "Propriété à vérifier"}
                            >
                              ⚠ Propriété à vérifier
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        {o.manques === 0 ? (
                          <span className="text-emerald-700 font-semibold text-sm">Complet</span>
                        ) : (
                          <span className="inline-flex items-center justify-center min-w-[1.75rem] rounded-full bg-amber-100 text-amber-800 font-bold text-sm px-2 py-0.5">{o.manques}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Cartes de transmission des héritiers — visibles seulement si le client
            a explicitement autorisé l'étude (RLS heir_transmission_grants). */}
        <NotaireTransmissionCartes clientId={clientId} />

        <p className="mt-8 text-xs text-gray-400 max-w-2xl">
          ARTKIV prépare et structure. Le commissaire-priseur ou l'expert mandaté contrôle et valide les éléments
          relevant de sa compétence. Aucune valeur n'est affichée ici : le dossier de préparation ne se substitue pas à l'expertise.
        </p>
      </main>
    </div>
  );
}
